import { ScanResult, ScanSummary, createScanSummary } from './types';
import { getPlatform } from '@/utils/pwaDetection';

export function runPWAInstallScan(): ScanSummary {
  console.group('📱 PWA Install Instruction Scan');
  
  const results: ScanResult[] = [];

  try {
    // Check 1: Platform detection
    const platform = getPlatform();

    results.push({
      name: 'Platform Detection',
      status: 'pass',
      message: `Detected platform: ${platform}`,
      details: `User agent: ${navigator.userAgent}`
    });

    // Check 2: PWA installability
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                        (window.navigator as any).standalone === true;

    results.push({
      name: 'PWA Install Status',
      status: 'info',
      message: isStandalone ? 'App is installed (standalone mode)' : 'App is not installed',
      details: isStandalone ? 'Running in PWA mode' : 'Running in browser'
    });

    // Check 3: Manifest file
    const manifestLink = document.querySelector('link[rel="manifest"]');
    results.push({
      name: 'Manifest File',
      status: manifestLink ? 'pass' : 'fail',
      message: manifestLink ? 'Manifest link found in HTML' : 'Manifest link missing',
      details: manifestLink ? `href: ${manifestLink.getAttribute('href')}` : 'No manifest detected'
    });

    // Check 4: Service Worker
    const isDev = import.meta.env.DEV || location.hostname === 'localhost';
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then(registration => {
        results.push({
          name: 'Service Worker',
          status: registration ? 'pass' : (isDev ? 'info' : 'warn'),
          message: registration ? 'Service Worker registered' : (isDev ? 'Service Worker not registered (dev mode)' : 'Service Worker not registered'),
          details: registration ? `Scope: ${registration.scope}` : (isDev ? 'Service workers are typically disabled in development' : 'No active service worker')
        });
      });
    } else {
      results.push({
        name: 'Service Worker',
        status: 'fail',
        message: 'Service Worker API not supported',
        details: 'Browser does not support service workers'
      });
    }

    // Check 5: Install prompt capability
    const hasInstallPrompt = 'BeforeInstallPromptEvent' in window;
    results.push({
      name: 'Install Prompt Support',
      status: hasInstallPrompt ? 'pass' : 'info',
      message: hasInstallPrompt ? 'Browser supports install prompt' : 'Browser does not support install prompt',
      details: 'BeforeInstallPromptEvent API availability'
    });

    // Check 6: Platform-specific instructions availability
    const platformInstructions: Record<string, string> = {
      'ios-safari': 'Tap Share → Add to Home Screen',
      'ios-other': 'Open in Safari → Share → Add to Home Screen',
      'android-chrome': 'Tap menu (⋮) → Install app',
      'android-firefox': 'Tap menu → Install',
      'desktop-chrome': 'Click install icon in address bar',
      'desktop-edge': 'Click install icon in address bar',
      'desktop-safari': 'File menu → Add to Dock',
      'other': 'Check browser menu for install option'
    };

    const instruction = platformInstructions[platform] || platformInstructions['other'];

    results.push({
      name: 'Install Instructions',
      status: 'info',
      message: `Instructions for ${platform}: ${instruction}`,
      details: 'Platform-specific installation guidance'
    });

    // Check 7: PWA icons
    const icons = document.querySelectorAll('link[rel*="icon"]');
    results.push({
      name: 'PWA Icons',
      status: icons.length > 0 ? 'pass' : 'warn',
      message: `Found ${icons.length} icon link(s)`,
      details: Array.from(icons).map(icon => icon.getAttribute('href')).join(', ')
    });

    // Check 8: Theme color
    const themeColor = document.querySelector('meta[name="theme-color"]');
    results.push({
      name: 'Theme Color',
      status: themeColor ? 'pass' : 'warn',
      message: themeColor ? 'Theme color meta tag found' : 'Theme color meta tag missing',
      details: themeColor ? `Color: ${themeColor.getAttribute('content')}` : 'Recommended for PWA appearance'
    });

  } catch (error) {
    results.push({
      name: 'PWA Install Scan Error',
      status: 'fail',
      message: `Failed to run PWA install scan: ${error}`,
      details: String(error)
    });
  }

  const summary = createScanSummary('PWA Install Instruction Scan', results);
  
  console.log(`✅ Passed: ${summary.passCount}`);
  console.log(`⚠️  Warnings: ${summary.warnCount}`);
  console.log(`ℹ️  Info: ${summary.infoCount}`);
  console.table(results);
  console.groupEnd();
  
  return summary;
}
